import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { getToken } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Layers, Zap, FileQuestion, BookOpen, TrendingUp, Eye, ArrowRight } from "lucide-react";
import BrainLogo from "@/components/BrainLogo";
import GemIcon from "@/components/GemIcon";
import RarityBadge, { rarityMeta } from "@/components/RarityBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/Seo";
import JsonLd from "@/components/JsonLd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PackView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [pack, setPack] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shared-packs/${slug}`).then((r) => setPack(r.data)).catch(() => {
      toast.error("Pack not found");
      nav("/");
    });
  }, [slug, nav]);

  const handleImport = async () => {
    if (!user) { nav(`/signup?next=/packs/${slug}`); return; }
    setImporting(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/shared-packs/${slug}/import`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(price > 0 ? `Imported! ${price} gems sent to publisher.` : "Imported to your space");
      try { await refresh?.(); } catch {/*ignore*/}
      nav(`/app/studies/${data.sc_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import");
    } finally {
      setImporting(false);
    }
  };

  if (!pack) return <div className="min-h-screen bg-[#0A0A0B]" />;

  const counts = {
    cards: (pack.flashcards || []).length,
    quiz: (pack.quiz_questions || []).length,
    glossary: (pack.glossary || []).length,
    exam: (pack.likely_exam_questions || []).length,
  };
  const price = pack.price_gems || 0;
  const isFree = price === 0;
  const userGems = user?.gems || 0;
  const canAfford = isFree || userGems >= price;
  const meta = rarityMeta(pack.rarity);

  const seoBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  const seoUrl = `${seoBase}/packs/${slug}`;
  const seoDesc =
    (pack.description || pack.summary_standard || "").slice(0, 155) ||
    `${pack.title} — a ${pack.is_course ? "course bundle" : "study pack"} with ${counts.cards} flashcards, ${counts.quiz} quiz questions and predicted exam questions on Studivault.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    name: pack.title,
    description: seoDesc,
    url: seoUrl,
    learningResourceType: pack.is_course ? "Course" : "Study guide",
    inLanguage: "en",
    keywords: (pack.tags || []).join(", ") || undefined,
    isAccessibleForFree: isFree,
    author: { "@type": "Person", name: pack.owner_name || "Studivault user" },
    provider: { "@type": "Organization", name: "Studivault", url: seoBase },
    interactionStatistic: [
      { "@type": "InteractionCounter", interactionType: "https://schema.org/ViewAction", userInteractionCount: pack.views || 0 },
      { "@type": "InteractionCounter", interactionType: "https://schema.org/DownloadAction", userInteractionCount: pack.imports || 0 },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5]">
      <Seo title={pack.title} description={seoDesc} path={`/packs/${slug}`} image={`${seoBase}/icon-512.png`} />
      <JsonLd id="pack-jsonld" data={jsonLd} />
      <header className="border-b border-white/5 px-5 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav(-1)}
            data-testid="pack-back-btn"
            aria-label="Go back"
            className="btn-icon mr-1 text-[#B0AEBC] hover:text-[#B8A4FF]"
          >
            <ArrowLeft size={18} />
          </button>
          <Link to="/" className="flex items-center gap-2.5">
            <BrainLogo size={26} />
            <span className="font-semibold tracking-tight">Studivault</span>
          </Link>
        </div>
        <div className="flex items-center gap-3 text-[12px] text-[#8E8B99]">
          <Eye size={12} /> {pack.views} views · {pack.imports} imports
        </div>
      </header>

      <div className="max-w-[920px] mx-auto px-5 md:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="eyebrow">{pack.is_course ? "Course bundle" : "Shared study"}</div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border ${pack.is_course ? "bg-[#5AC8FA]/15 border-[#5AC8FA]/45 text-[#5AC8FA]" : "bg-white/10 border-white/20 text-[#D8D6E0]"}`}>
              {pack.is_course ? "Course" : "Study"}
            </span>
            {pack.rarity && <RarityBadge rarity={pack.rarity} size="sm" />}
            {isFree ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] bg-[#34D399]/15 border border-[#34D399]/40 text-[#34D399]" data-testid="pack-price-free">FREE</span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[12px] font-bold tabular-nums bg-[#7C5CFF]/12 border border-[#7C5CFF]/40 text-[#7C5CFF]" data-testid={`pack-price-${price}`}>
                <GemIcon size={11} /> {price} {price === 1 ? "gem" : "gems"}
              </span>
            )}
          </div>
          <h1 className="display-2 mt-1">{pack.title}</h1>
          {pack.rarity_reason && (
            <p className="text-[12.5px] text-[#71717A] mt-1.5 italic" data-testid="pack-rarity-reason">
              <span style={{ color: meta.color }}>{meta.label}</span> — {pack.rarity_reason}
            </p>
          )}
          {pack.description && <p className="text-[15px] text-[#B0AEBC] mt-3">{pack.description}</p>}
          <p className="text-[13px] text-[#8E8B99] mt-3">by <span className="text-[#B8A4FF] font-medium">@{(pack.owner_name || "anon").split(" ")[0].toLowerCase()}</span></p>

          <div className="flex flex-wrap gap-2 mt-5">
            {(pack.tags || []).slice(0, 8).map((t, i) => <span key={i} className="chip chip-blue">{t}</span>)}
          </div>

          <div className="grid grid-cols-4 gap-3 mt-7">
            <Tile icon={Zap}        label="Cards"    value={counts.cards} />
            <Tile icon={FileQuestion} label="Quiz Qs" value={counts.quiz} />
            <Tile icon={BookOpen}   label="Glossary" value={counts.glossary} />
            <Tile icon={TrendingUp} label="Exam Qs"  value={counts.exam} />
          </div>

          <motion.button
            whileHover={{ scale: canAfford ? 1.02 : 1 }}
            whileTap={{ scale: canAfford ? 0.98 : 1 }}
            onClick={handleImport}
            disabled={importing || (user && !canAfford)}
            data-testid="import-pack-btn"
            className="btn btn-primary mt-8 px-6 py-3 disabled:opacity-50"
            style={!canAfford && user ? { background: "rgba(252,165,165,0.18)", border: "1px solid rgba(252,165,165,0.4)", boxShadow: "none" } : undefined}
          >
            {importing ? (
              "Importing…"
            ) : !user ? (
              <>Sign up & import <ArrowRight size={13} /></>
            ) : !canAfford ? (
              <>Need {price - userGems} more gems</>
            ) : isFree ? (
              <>Import to my space <ArrowRight size={13} /></>
            ) : (
              <>Import for <GemIcon size={12} /> {price} <ArrowRight size={13} /></>
            )}
          </motion.button>
          {!isFree && user && (
            <div className="text-[11.5px] text-[#71717A] mt-2">
              Your balance: <span className="text-white font-semibold">{userGems} gems</span> — paid immediately to <span className="text-[#B8A4FF]">@{(pack.owner_name || "anon").split(" ")[0].toLowerCase()}</span>.
            </div>
          )}
        </motion.div>

        {pack.summary_standard && (
          <section className="mt-12">
            <div className="eyebrow-muted mb-3">Preview</div>
            <p className="text-[14.5px] leading-relaxed text-[#E4E4E7] whitespace-pre-wrap">{pack.summary_standard}</p>
          </section>
        )}

        {pack.notes_markdown && (
          <section className="mt-10">
            <div className="eyebrow-muted mb-3">First notes</div>
            <div className="surface p-6">
              <p className="text-[13.5px] text-[#B0AEBC] leading-relaxed whitespace-pre-line">
                {pack.notes_markdown.split("\n").slice(0, 20).join("\n")}
                {pack.notes_markdown.split("\n").length > 20 && "\n\n… import to see the full notes, cards, quiz, glossary and exam questions."}
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Tile = ({ icon: Icon, label, value }) => (
  <div className="surface p-4 text-center">
    <Icon size={16} className="text-[#B8A4FF] mx-auto mb-2" />
    <div className="text-[22px] font-bold tabular-nums text-white">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-[#8E8B99] mt-1">{label}</div>
  </div>
);
